import React, { useState, useEffect } from 'react';
import DiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import {
  Box,
  Typography,
  Button,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Divider,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import './FileComparePage.css';

const FileComparePage = () => {
  const [compareData, setCompareData] = useState(null);
  const [viewMode, setViewMode] = useState('split');
  const [showDiffOnly, setShowDiffOnly] = useState(false);
  const [showEntireFile, setShowEntireFile] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    const data = sessionStorage.getItem('compareData');
    if (data) {
      setCompareData(JSON.parse(data));
    } else {
      navigate('/');
    }
  }, [navigate]);

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleClose = () => {
    sessionStorage.removeItem('compareData');
    window.close();
  };

  if (!compareData) {
    return (
      <Box className="compare-loading">
        <Typography>Loading comparison...</Typography>
      </Box>
    );
  }

  const customStyles = {
    variables: {
      light: {
        codeFoldGutterBackground: '#f7f7f7',
        codeFoldBackground: '#f1f8ff',
        gutterBackground: '#f7f7f7',
        gutterBackgroundDark: '#f3f1f1',
        highlightBackground: '#fffbdd',
        highlightGutterBackground: '#ffcd3c',
        lineNumberColor: '#666',
        lineNumberColorNew: '#16a085',
        lineNumberColorOld: '#c0392b',
        wordAddedBackground: '#e6ffed',
        wordRemovedBackground: '#ffeef0',
        addedBackground: '#d4edda',
        removedBackground: '#f8d7da',
        addedGutterBackground: '#cdffd8',
        removedGutterBackground: '#fdb8c0',
      },
      dark: {
        diffViewerBackground: '#1e1e1e',
        diffViewerColor: '#e0e0e0',
        codeFoldGutterBackground: '#2d2d2d',
        codeFoldBackground: '#263238',
        codeFoldContentColor: '#aaa',
        gutterBackground: '#252525',
        gutterBackgroundDark: '#1a1a1a',
        gutterColor: '#888',
        highlightBackground: '#3e3d32',
        highlightGutterBackground: '#4d4522',
        lineNumberColor: '#888',
        lineNumberColorNew: '#2ecc71',
        lineNumberColorOld: '#e74c3c',
        wordAddedBackground: '#1a4d2e',
        wordRemovedBackground: '#4d1a1a',
        addedBackground: '#1a3a2a',
        removedBackground: '#3a1a1a',
        addedGutterBackground: '#1a4d2e',
        removedGutterBackground: '#4d1a1a',
        emptyLineBackground: '#252525',
      }
    },
    diffContainer: {
      height: '100%',
      overflow: 'auto',
      fontSize: '14px',
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '6px'
    },
    splitView: {
      fontSize: '14px',
      height: '100%'
    },
    line: {
      fontSize: '14px',
      lineHeight: '20px',
      wordBreak: 'break-all'
    },
    marker: {
      fontSize: '12px'
    },
    gutter: {
      minWidth: '50px',
      textAlign: 'center'
    }
  };

  const lines1 = compareData.content1.split('\n');
  const lines2 = compareData.content2.split('\n');

  return (
    <Box className="compare-page">
      <Paper elevation={2} className="compare-header">
        <Box className="compare-header-top">
          <Typography variant="h4" color="primary">
            File Comparison
          </Typography>
          <Button variant="contained" color="primary" onClick={handleClose}>
            Close Comparison
          </Button>
        </Box>

        <Box className="compare-file-chips">
          <Chip
            label={`File 1: ${compareData.filename1}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`File 2: ${compareData.filename2}`}
            color="secondary"
            variant="outlined"
          />
        </Box>

        <Divider className="compare-divider" />

        <Box className="compare-view-toggle">
          <Typography variant="body2" color="text.secondary">
            View Mode:
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="split">Split View</ToggleButton>
            <ToggleButton value="unified">Unified View</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      <Box className="compare-summary">
        <Typography variant="caption" color="text.secondary">
          File 1: {compareData.content1?.length || 0} characters, {lines1.length} lines
        </Typography>
        <Typography variant="caption" color="text.secondary">
          File 2: {compareData.content2?.length || 0} characters, {lines2.length} lines
        </Typography>
      </Box>

      <Box className="compare-viewer">
        <DiffViewer
          oldValue={compareData.content1}
          newValue={compareData.content2}
          splitView={viewMode === 'split'}
          showDiffOnly={showDiffOnly}
          useDarkTheme={isDark}
          leftTitle={`📄 ${compareData.filename1}`}
          rightTitle={`📄 ${compareData.filename2}`}
          compareMethod={DiffMethod.WORDS}
          styles={{
            ...customStyles,
            diffContainer: {
              ...customStyles.diffContainer,
              height: '100%',
              maxHeight: 'none'
            }
          }}
          hideLineNumbers={false}
          disableWordDiff={false}
          extraLinesSurroundingDiff={showEntireFile ? Number.MAX_SAFE_INTEGER : 3}
          codeFoldMessageRenderer={
            !showEntireFile
              ? (totalFoldedLines) =>
                  `⚡ ${totalFoldedLines} lines hidden - click to expand`
              : null
          }
        />
      </Box>
    </Box>
  );
};

export default FileComparePage;
